import React, { useEffect, useState } from 'react';

import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import Loader from 'components/Loader';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { AvailableProcessingActivity } from 'config/api/assetRopa/assetRopaWorkspace.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

type Props = {
  assetRopaId: number;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (processingActivityId: number) => Promise<'success' | 'unavailable' | 'failed' | void>;
  open: boolean;
  organizationId?: number;
};

const useStyles = makeStyles(theme => ({
  option: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  optionMeta: {
    color: theme.palette.text.secondary,
  },
  preview: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  previewSection: {
    marginTop: theme.spacing(1.5),
  },
  actionsRow: {
    marginTop: theme.spacing(1),
  },
}));

const AddProcessingActivityModal: React.FC<Props> = ({ assetRopaId, loading = false, onClose, onConfirm, open, organizationId }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.add_processing_activity;
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AvailableProcessingActivity[]>([]);
  const [selected, setSelected] = useState<AvailableProcessingActivity | null>(null);
  const [unavailableError, setUnavailableError] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(inputValue.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setInputValue('');
    setDebouncedSearch('');
    setPage(1);
    setItems([]);
    setSelected(null);
    setUnavailableError(false);
  }, [assetRopaId, open]);

  const availableQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_AVAILABLE_PROCESSING_ACTIVITIES, organizationId, assetRopaId, debouncedSearch, page],
    async () => {
      const { data } = await assetRopaApi.getAvailableProcessingActivities(organizationId as number, assetRopaId, {
        search: debouncedSearch || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      return data;
    },
    {
      enabled: Boolean(open && organizationId && assetRopaId),
      retry: false,
    },
  );

  useEffect(() => {
    if (!availableQuery.data) {
      return;
    }

    setItems(current => {
      if (page === 1) {
        return availableQuery.data.results;
      }

      const seen = new Set(current.map(item => item.id));
      return [...current, ...availableQuery.data.results.filter(item => !seen.has(item.id))];
    });
  }, [availableQuery.data, page]);

  const hasMore = Boolean(availableQuery.data?.next);
  const isInitialLoading = availableQuery.isLoading && page === 1 && items.length === 0;

  const handleConfirm = async () => {
    if (!selected) return;

    const result = await onConfirm(selected.id);
    if (result === 'unavailable') {
      setUnavailableError(true);
      setPage(1);
      await availableQuery.refetch();
    }
  };

  const handleRefresh = async () => {
    setUnavailableError(false);
    setPage(1);
    await availableQuery.refetch();
  };

  const renderPreview = () => {
    if (!selected) {
      return null;
    }

    const roleLabel = getMessageFromMap(processing_activities_messages.processing_role, selected.processingRole, t);
    const versionLabel =
      selected.publishedVersionNumber != null
        ? t(messages.published_version, { number: selected.publishedVersionNumber })
        : t(processing_activities_messages.review_placeholder);

    return (
      <Box className={styles.preview}>
        <Typography variant='subtitle2'>{t(messages.preview_title)}</Typography>
        <Typography color='textSecondary' variant='body2'>
          {versionLabel}
        </Typography>
        <Typography className={styles.previewSection} variant='body2'>
          {selected.description || t(processing_activities_messages.review_placeholder)}
        </Typography>
        <Typography className={styles.previewSection} variant='body2'>
          {roleLabel}
        </Typography>
        <div className={styles.previewSection}>
          <Typography variant='caption'>{t(messages.purposes)}</Typography>
          {selected.purposes.length === 0 ? (
            <Typography variant='body2'>{t(processing_activities_messages.review_placeholder)}</Typography>
          ) : (
            selected.purposes.map(purpose => (
              <Typography key={`${purpose.name}-${purpose.legalBasis}`} variant='body2'>
                {purpose.name} — {getMessageFromMap(processing_activities_messages.legal_basis, purpose.legalBasis, t)}
              </Typography>
            ))
          )}
        </div>
        <div className={styles.previewSection}>
          <Typography variant='caption'>{t(messages.data_subject_categories)}</Typography>
          <Typography variant='body2'>
            {selected.dataSubjectCategories.join(', ') || t(processing_activities_messages.review_placeholder)}
          </Typography>
        </div>
        <div className={styles.previewSection}>
          <Typography variant='caption'>{t(messages.personal_data_categories)}</Typography>
          <Typography variant='body2'>
            {selected.personalDataCategories.join(', ') || t(processing_activities_messages.review_placeholder)}
          </Typography>
        </div>
        <div className={styles.previewSection}>
          <Typography variant='caption'>{t(messages.recipient_categories)}</Typography>
          <Typography variant='body2'>
            {selected.recipientCategories.join(', ') || t(processing_activities_messages.review_placeholder)}
          </Typography>
        </div>
      </Box>
    );
  };

  const renderContent = () => {
    if (isInitialLoading) {
      return <Loader inner />;
    }

    if (availableQuery.isError && items.length === 0) {
      return t(processing_activities_messages.asset_ropa.errors.load_failed);
    }

    return (
      <>
        <Autocomplete
          filterOptions={options => options}
          getOptionLabel={option => option.name}
          getOptionSelected={(option, value) => option.id === value.id}
          inputValue={inputValue}
          loading={availableQuery.isFetching}
          noOptionsText={debouncedSearch ? t(messages.search_empty) : t(messages.empty)}
          onChange={(_event, value) => {
            setSelected(value);
            setUnavailableError(false);
          }}
          onInputChange={(_event, value, reason) => {
            if (reason !== 'reset') {
              setInputValue(value);
            }
          }}
          options={items}
          renderInput={params => (
            <TextField {...params} label={t(messages.select_label)} placeholder={t(messages.search_placeholder)} variant='outlined' />
          )}
          renderOption={option => (
            <div className={styles.option}>
              <Typography variant='body2'>{option.name}</Typography>
              <Typography className={styles.optionMeta} variant='caption'>
                {getMessageFromMap(processing_activities_messages.processing_role, option.processingRole, t)}
                {option.description ? ` — ${option.description}` : ''}
              </Typography>
            </div>
          )}
          value={selected}
        />
        {hasMore && (
          <div className={styles.actionsRow}>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={availableQuery.isFetching}
              icon={false}
              onClick={() => setPage(current => current + 1)}
              showLoader={availableQuery.isFetching && page > 1}
              textLike={false}
              variant='outlined'
            >
              {t(messages.load_more)}
            </ColoredButton>
          </div>
        )}
        {unavailableError && (
          <>
            <FormHelperText error>{t(messages.unavailable)}</FormHelperText>
            <div className={styles.actionsRow}>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled={availableQuery.isFetching}
                icon={false}
                onClick={handleRefresh}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                {t(messages.refresh)}
              </ColoredButton>
            </div>
          </>
        )}
        {renderPreview()}
      </>
    );
  };

  return (
    <Dialog fullWidth maxWidth='md' onClose={onClose} open={open}>
      <DialogTitle>{t(messages.title)}</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={onClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || !selected}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(messages.confirm)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddProcessingActivityModal;
